<template>
    <div class="my-2 rounded-md border-l-[3px] p-3 text-xs" :class="error ? 'border-red-500 bg-red-50' : record ? 'border-green-500 bg-green-50/50' : 'border-blue-500 bg-blue-50/50'">
        <div class="flex items-center gap-2">
            <UIcon
                :name="error ? 'i-heroicons-exclamation-circle' : record ? 'i-heroicons-document-arrow-down' : 'i-heroicons-arrow-path'"
                class="h-4 w-4 shrink-0"
                :class="error ? 'text-red-500' : record ? 'text-green-600' : 'animate-spin text-blue-500'"
            />
            <span class="font-medium text-slate-700">CSV export</span>
        </div>
        <div v-if="record" class="mt-2 flex flex-wrap items-center gap-3">
            <div class="min-w-0 flex-1">
                <p class="break-words font-medium text-slate-700">{{ record.filename }}</p>
                <p class="mt-0.5 text-slate-500">{{ record.count.toLocaleString() }} {{ record.count === 1 ? 'row' : 'rows' }}</p>
            </div>
            <UButton size="sm" color="primary" icon="i-heroicons-arrow-down-tray" :loading="downloading" :disabled="downloading" @click="download">
                Download CSV
            </UButton>
        </div>
        <p v-else-if="!error" class="mt-2 text-slate-500" role="status">Preparing your CSV…</p>
        <div v-if="error" class="mt-2 text-red-600" role="alert">
            <p v-if="statusError" class="mb-1">Could not check status. Your export may still be preparing.</p>
            <p class="break-words">{{ error }}</p>
            <UButton v-if="statusError && exportState?.status === 'pending'" class="mt-2" size="sm" color="neutral" variant="outline" :loading="checking" @click="checkStatus">
                Check status
            </UButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ExportRecordSummary, ExportRunStatus } from '@coldreach/campaigns';

// Chat history and HTTP responses serialize createdAt to a string.
type ExportRecord = Omit<ExportRecordSummary, 'createdAt'> & { createdAt: Date | string };
type ExportState = Exclude<ExportRunStatus, { status: 'ready' }>
    | { status: 'ready'; runId?: string; record: ExportRecord };

const props = defineProps<{
    initialState?: ExportState;
    errorMessage?: string;
}>();

const exportState = ref(props.initialState);
const statusError = ref('');
const downloadError = ref('');
const checking = ref(false);
const downloading = ref(false);
const controller = new AbortController();
let pollTimer: ReturnType<typeof setTimeout> | undefined;

const toolError = computed(() => props.errorMessage || '');
const record = computed(() => !toolError.value && exportState.value?.status === 'ready' ? exportState.value.record : undefined);
const error = computed(() => toolError.value || statusError.value || downloadError.value || (exportState.value?.status === 'failed' ? exportState.value.error : ''));

async function checkStatus() {
    if (checking.value || toolError.value || exportState.value?.status !== 'pending' || controller.signal.aborted) return;
    clearTimeout(pollTimer);
    checking.value = true;
    statusError.value = '';
    try {
        exportState.value = await $fetch<ExportState>(`/api/exports/runs/${encodeURIComponent(exportState.value.runId)}`, {
            signal: controller.signal,
            retry: 0,
        });
        // Schedule after the request settles so slow requests never overlap.
        if (!controller.signal.aborted && exportState.value.status === 'pending') {
            pollTimer = setTimeout(checkStatus, 3000);
        }
    } catch (err) {
        if (!controller.signal.aborted) {
            statusError.value = err instanceof Error ? err.message : 'Could not check the export status.';
        }
    } finally {
        checking.value = false;
    }
}

async function download() {
    if (!record.value || downloading.value || controller.signal.aborted) return;
    const { id, filename } = record.value;
    downloading.value = true;
    downloadError.value = '';
    try {
        // Only this authenticated endpoint is allowed; never use a tool-provided URL.
        const blob = await $fetch<Blob>(`/api/exports/${id}/download`, {
            responseType: 'blob',
            signal: controller.signal,
            retry: 0,
        });
        if (!controller.signal.aborted) downloadBlob(blob, filename);
    } catch (err) {
        if (!controller.signal.aborted) {
            downloadError.value = err instanceof Error ? err.message : 'Could not download the CSV. Please try again.';
        }
    } finally {
        downloading.value = false;
    }
}

onMounted(() => {
    if (exportState.value?.status === 'pending') void checkStatus();
});

onBeforeUnmount(() => {
    clearTimeout(pollTimer);
    controller.abort();
});
</script>
